import type { CSSProperties, ReactElement } from 'react';
import { useTheme } from '@course-authoring/shared-types';
import { useTranslation } from '@course-authoring/i18n';
import { LineChartIcon } from './LineChartIcon';

export interface ResponseThresholdEmptyStateProps {
  className?: string;
  style?: CSSProperties;
  /**
   * 'loading' renders a plain "fetching results" message with no icon,
   * shown briefly while the real results fetch is in flight - avoids
   * flashing fake full-graph placeholder data before the real (possibly
   * below-threshold) result arrives. 'checking-answer' (NA-319) renders a
   * plain "checking for a previous answer" message, shown in place of a
   * question block's entire controls/results area while the player's
   * bootstrap prior-answers lookup is still in flight, so a learner can't
   * start answering a question that's about to reveal itself as already
   * answered. Defaults to the below-threshold "not enough responses" message.
   */
  variant?: 'empty' | 'loading' | 'checking-answer';
}

/**
 * Placeholder shown in place of aggregate results on Likert, WCS, and
 * ungated Single Choice blocks when the account's response threshold
 * (NA-72) hasn't been met — the block's own-answer indicator continues to
 * render outside of this component, unaffected.
 */
export function ResponseThresholdEmptyState({
  className,
  style,
  variant = 'empty',
}: ResponseThresholdEmptyStateProps): ReactElement {
  const { t } = useTranslation();
  const { tokens } = useTheme();
  const isCheckingAnswer = variant === 'checking-answer';
  const isLoading = variant === 'loading' || isCheckingAnswer;

  return (
    <div
      className={className}
      style={{
        backgroundColor: tokens.backgrounds.card,
        border: `1px solid ${tokens.borders.default}`,
        borderRadius: tokens.radius.lg,
        padding: '40px 24px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: tokens.spacing.md,
        width: '100%',
        boxSizing: 'border-box',
        ...style,
      }}
      data-testid={
        isCheckingAnswer
          ? 'question-answer-status-loading'
          : isLoading
            ? 'response-results-loading'
            : 'response-threshold-empty-state'
      }
    >
      {isLoading ? null : (
        <LineChartIcon width={40} height={40} style={{ color: tokens.text.primary }} />
      )}
      <p
        style={{
          margin: 0,
          color: isLoading ? tokens.text.muted : tokens.text.primary,
          fontFamily: tokens.typography.fontFamily,
          fontSize: '16px',
          fontWeight: 400,
          lineHeight: '1.4',
          textAlign: 'center',
        }}
      >
        {isCheckingAnswer
          ? t('questionnaire.loading')
          : isLoading
            ? t('response_results.loading')
            : t('response_results.not_enough_responses')}
      </p>
    </div>
  );
}

ResponseThresholdEmptyState.displayName = 'ResponseThresholdEmptyState';
